import React from "react"
import { useEffect, useRef } from "react"
import { useTheme } from "../context/ThemeContext"

/**
 * Full-viewport 3D particle field with depth scaling and connection lines.
 */
export default function ParticleBackground({ particleCount = 90, className = "" }) {
  const canvasRef = useRef(null)
  const { isDark } = useTheme()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    let animationFrameId
    let particles = []

    const particlePalette = isDark
      ? ["45, 212, 191", "56, 189, 248"]
      : ["59, 158, 255", "186, 224, 255"]

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener("resize", resizeCanvas)

    class Particle3D {
      constructor() {
        this.reset(true)
      }

      reset(initial = false) {
        this.x = Math.random() * canvas.width
        this.y = Math.random() * canvas.height
        this.z = initial ? Math.random() * 1000 : 1000
        this.size = Math.random() * 2.5 + 0.8
        this.speedZ = Math.random() * 1.8 + 0.4
        const rgb = particlePalette[Math.floor(Math.random() * particlePalette.length)]
        this.color = `rgba(${rgb}, ${Math.random() * 0.32 + 0.18})`
        this.rgb = rgb
      }

      update() {
        this.z -= this.speedZ
        if (this.z <= 0) this.reset()
      }

      project() {
        const scale = 1000 / (1000 - this.z)
        return {
          x: (this.x - canvas.width / 2) * scale + canvas.width / 2,
          y: (this.y - canvas.height / 2) * scale + canvas.height / 2,
          size: this.size * scale,
          scale,
        }
      }

      draw() {
        const { x, y, size } = this.project()
        ctx.beginPath()
        ctx.arc(x, y, size, 0, Math.PI * 2)
        ctx.fillStyle = this.color
        ctx.shadowBlur = 14
        ctx.shadowColor = this.color
        ctx.fill()
        ctx.shadowBlur = 0
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle3D())
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const projected = particles.map((p) => {
        p.update()
        return { particle: p, ...p.project() }
      })

      projected.forEach(({ particle }) => particle.draw())

      for (let i = 0; i < projected.length; i++) {
        for (let j = i + 1; j < projected.length; j++) {
          const a = projected[i]
          const b = projected[j]
          const dx = a.particle.x - b.particle.x
          const dy = a.particle.y - b.particle.y
          const dz = a.particle.z - b.particle.z
          const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)

          if (distance < 140) {
            const alpha = 0.16 * (1 - distance / 140) * Math.min(a.scale, b.scale)
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.strokeStyle = `rgba(${a.particle.rgb}, ${alpha})`
            ctx.lineWidth = 0.8
            ctx.stroke()
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener("resize", resizeCanvas)
      cancelAnimationFrame(animationFrameId)
    }
  }, [isDark, particleCount])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={`fixed inset-0 w-full h-full pointer-events-none ${className}`}
    />
  )
}
